import { AnimatedText, AnimatedTitle } from "@/components/ui/AnimatedText";
import { useSEO } from "@/utils/useSEO";
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";

export default function FAQ() {
  const { t, i18n } = useTranslation();
  useSEO(
    "Frequently Asked Questions",
    "Find answers to common questions about staying at Patal Kikian Villas Sidemen. Learn about check-in times, included amenities, nearby activities, transportation, and everything you need to plan your perfect Bali villa getaway."
  );
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const getPath = (path: string) => {
    if (i18n.language === 'en' || !i18n.language) return path;
    return `/${i18n.language}${path}`;
  };

  const faqs = [
    {
      question: t("faq.q1.question", "What time is check-in and check-out?"),
      answer: t("faq.q1.answer", "Check-in time is at 2:00 PM, and check-out is at 12:00 PM (noon). Early check-in or late check-out is subject to availability and may incur additional charges.")
    },
    {
      question: t("faq.q2.question", "Is breakfast included in the room rate?"),
      answer: t("faq.q2.answer", "Yes, a complimentary daily breakfast is included with all of our villa bookings.")
    },
    {
      question: t("faq.q3.question", "Do you provide airport transfers?"),
      answer: t("faq.q3.answer", "Yes, we offer airport transfer services for an additional fee. Please contact us in advance to arrange your pick-up.")
    },
    {
      question: t("faq.q4.question", "Is Patal Kikian family-friendly?"),
      answer: t("faq.q4.answer", "While we welcome guests of all ages, our property is designed as a tranquil retreat. Parents must supervise children at all times, especially near the pool area.")
    },
    {
      question: t("faq.q5.question", "Do you accommodate dietary restrictions?"),
      answer: t("faq.q5.answer", "Absolutely. Our restaurant can cater to various dietary requirements including vegetarian, vegan, and gluten-free. Please inform us of your needs prior to arrival.")
    },
    {
      question: t("faq.q6.question", "Is Wi-Fi available at the villa?"),
      answer: t("faq.q6.answer", "Yes, complimentary high-speed Wi-Fi is available throughout the property, including in all rooms and public areas.")
    }
  ];

  return (
    <div className="min-h-[100dvh] bg-background text-white selection:bg-gold/30 selection:text-gold pt-32 pb-24">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-16">
          <AnimatedText delay={0.1}>
            <span className="text-gold uppercase tracking-widest text-sm font-semibold mb-4 block">
              {t("faq.subtitle", "Information")}
            </span>
          </AnimatedText>
          <AnimatedTitle title={t("faq.title", "Frequently Asked Questions")} className="text-4xl md:text-5xl font-luxury mb-6" />
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <AnimatedText key={index} delay={0.2 + (index * 0.1)}>
              <div 
                className={`border border-white/10 rounded-lg overflow-hidden transition-colors duration-300 ${openIndex === index ? 'bg-secondary/40 border-gold/30' : 'bg-transparent hover:bg-white/5'}`}
              >
                <button
                  className="w-full px-6 py-5 flex justify-between items-center text-left cursor-pointer"
                  onClick={() => toggleFaq(index)}
                >
                  <span className={`font-medium ${openIndex === index ? 'text-gold' : 'text-white'}`}>
                    {faq.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp className="text-gold flex-shrink-0" size={20} />
                  ) : (
                    <ChevronDown className="text-white/60 flex-shrink-0" size={20} />
                  )}
                </button>
                <div 
                  className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-white/70 text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </AnimatedText>
          ))}
        </div>
        
        <AnimatedText delay={0.8} className="mt-16 text-center">
          <p className="text-white/60 mb-6">{t("faq.stillHaveQuestions", "Still have questions?")}</p>
          <LocalLink to={getPath("/contact")} className="inline-flex items-center justify-center border border-gold text-gold font-medium rounded-sm hover:bg-gold hover:text-background transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-sm">
            {t("faq.btnContact", "Contact Us")}
          </LocalLink>
        </AnimatedText>
      </div>
    </div>
  );
}
